import PropTypes from 'prop-types';
import Link from '@activebrands/core-web/components/Link';
import { OVERLAY_ID } from '@activebrands/core-web/config/overlay';
import overlay from '@activebrands/core-web/libs/overlay';
import { useStyletron } from '@activebrands/core-web/libs/styletron';
import SubMenuLevel from 'components/Header/Navigation/PrimaryMenu/SubMenuLevel';
import ThemeButton from 'components/buttons/ThemeButton';
import ArrowIcon from 'components/icons/ArrowIcon';
import transformPreviewEditable from '@activebrands/core-web/libs/storyblok/utils/transform-preview-editable';

const PrimaryMenu = ({ items = [], menuValue = {}, resetNavigation = () => null, setMenuValue }) => {
    const [css] = useStyletron();

    if (!items?.length) {
        return null;
    }

    return (
        <ul
            data-selector="global.nav.menu.primary"
            className={`${css({
                display: 'flex',
                flexDirection: 'column',
                margin: '0 0 24px 0',
            })} primary-site-menu`}
        >
            {items?.map(levelOneItem => {
                const hasSubmenu = levelOneItem.submenus?.length > 0;
                const isActive = menuValue.current === levelOneItem._uid;
                const subMenuIsActive = levelOneItem.submenus.some(subMenu => subMenu._uid === menuValue.current);
                const sbEditable = transformPreviewEditable(levelOneItem._editable);

                return (
                    <li className={css({ margin: '4px 0' })} key={levelOneItem.label} {...sbEditable}>
                        <div
                            className={css({
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'space-between',
                            })}
                        >
                            <ThemeButton
                                isVolatile
                                data-selector="global.nav.menu.primary.item"
                                theme="link"
                                aria-expanded={hasSubmenu ? isActive : undefined}
                                aria-haspopup={hasSubmenu ? 'menu' : undefined}
                                $style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    whiteSpace: 'normal',
                                    textAlign: 'left',
                                }}
                                fontKeys="Miscellaneous/24_100_-2"
                                active={isActive}
                                as={Link}
                                rel={levelOneItem.follow === 'no_follow' ? 'nofollow' : undefined}
                                to={levelOneItem?.link?.url}
                                onClick={e => {
                                    if (hasSubmenu) {
                                        overlay.open(OVERLAY_ID.NAVIGATION);
                                        e.preventDefault();
                                        setMenuValue({
                                            current: hasSubmenu ? levelOneItem._uid : null,
                                            prev: menuValue.current,
                                        });
                                    } else {
                                        resetNavigation();
                                    }
                                }}
                            >
                                <span className={css({ width: '80%' })}>{levelOneItem.label}</span>
                                {hasSubmenu && <ArrowIcon aria-hidden width="24px" />}
                            </ThemeButton>
                        </div>
                        <SubMenuLevel
                            featuredItems={levelOneItem.featuredItems}
                            featuredLabel={levelOneItem.featuredLabel}
                            isActive={isActive || subMenuIsActive}
                            level={1}
                            menuValue={menuValue}
                            parentLabel={levelOneItem.label}
                            parentLink={levelOneItem.link}
                            resetNavigation={resetNavigation}
                            setMenuValue={setMenuValue}
                            subMenuIsActive={subMenuIsActive}
                            submenus={levelOneItem.submenus}
                        />
                    </li>
                );
            })}
        </ul>
    );
};

PrimaryMenu.propTypes = {
    items: PropTypes.array.isRequired,
    menuValue: PropTypes.exact({
        current: PropTypes.string,
        prev: PropTypes.string,
    }).isRequired,
    resetNavigation: PropTypes.func.isRequired,
    setMenuValue: PropTypes.func.isRequired,
};

export default PrimaryMenu;
